import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Select, Flex, Typography, Table } from 'antd';
import { FolderOpen, File, Download, ChevronRight, Search, Upload, X, AlertCircle, Save, Bookmark } from 'lucide-react';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import api from '../../utils/api';
import { getUserInfo, getUserToken } from '../../reducers/User/actions';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

const IST_DATE_FORMAT = 'DD MMM YYYY, hh:mm A';

const formatUtcToIst = (modified: string, modifiedTimestamp?: number): string => {
  if (modifiedTimestamp) {
    const date = modifiedTimestamp > 1e12 ? dayjs.utc(modifiedTimestamp) : dayjs.unix(modifiedTimestamp).utc();
    return date.tz('Asia/Kolkata').format(IST_DATE_FORMAT);
  }

  if (!modified || modified === '-') {
    return '-';
  }

  const parsed = dayjs.utc(modified);
  return parsed.isValid() ? parsed.tz('Asia/Kolkata').format(IST_DATE_FORMAT) : modified;
};

interface FileItem {
  name: string;
  type: 'folder' | 'file';
  size: string;
  modified: string;
  owner: string;
  permissions: string;
  size_bytes?: number;
  modified_timestamp?: number;
}

interface Organization {
  name: string;
  account_id: string;
}

interface UploadProgressItem {
  name: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
}

interface SavedPath {
  path: string;
  service_type: string;
  name: string;
  saved_at: string;
}

interface SavedPaths {
  [orgId: string]: SavedPath[];
}

const FileAccessDashboard = () => {
  const userInfo: { role?: string } = useSelector(getUserInfo);
  const isAdmin = userInfo?.role === 'Admin';

  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [serviceType, setServiceType] = useState<'sftp' | 's3'>('sftp');
  const [currentPath, setCurrentPath] = useState<string>('');
  const [pathInput, setPathInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [pathHistory, setPathHistory] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgressItem>>({});
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [pendingUploads, setPendingUploads] = useState<File[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [savedPaths, setSavedPaths] = useState<SavedPaths>({});
  const [showSavePathModal, setShowSavePathModal] = useState<boolean>(false);
  const [pathToSave, setPathToSave] = useState<string>('');
  const [pathNameToSave, setPathNameToSave] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Fetch saved paths for the selected organization
  // We fetch all paths (without service_type filter) to cache them
  // and avoid refetching when switching between SFTP and S3
  const fetchSavedPaths = async () => {
    if (!selectedOrg) {
      return;
    }

    try {
      const response = await api.post('auth/paths/list', {
        organization_id: selectedOrg,
        // Note: service_type is optional - we don't pass it to get all paths
        // This allows us to cache all paths and filter on the frontend
      });
      if (response.status === 200 && response.saved_paths) {
        setSavedPaths((prev) => ({
          ...prev,
          [selectedOrg]: response.saved_paths || [],
        }));
      }
    } catch (error: any) {
      console.error('Failed to fetch saved paths:', error);
      // Don't show error to user for this, just log it
    }
  };

  // Load saved paths when organization changes
  useEffect(() => {
    fetchSavedPaths();
  }, [selectedOrg]);

  const filteredFiles = files.filter((file: FileItem) => file.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const ListOrganizations = async () => {
    const response = await api.post('/auth/organizations');

    console.log(response);
    setOrganizations(
      response?.organizations?.map((org: any, index: number) => ({
        name: org.name,
        account_id: org.account_id,
      })) || [],
    );
  };

  useEffect(() => {
    ListOrganizations();
  }, []);

  useEffect(() => {
    setSearchTerm('');
  }, [currentPath]);

  useEffect(() => {
    setCurrentPath('');
    setPathInput('');
    setFiles([]);
    setSelectedFiles(new Set());
    setPathHistory([]);
    setSearchTerm('');
    setError(null);
    setUploadProgress({});
    setPendingUploads([]);
    setShowUploadModal(false);
    setShowSavePathModal(false);
    setPathToSave('');
    setPathNameToSave('');
  }, [serviceType]);

  useEffect(() => {
    setCurrentPath('');
    setPathInput('');
    setFiles([]);
    setSelectedFiles(new Set());
    setPathHistory([]);
    setSearchTerm('');
    setError(null);
    setUploadProgress({});
    setPendingUploads([]);
    setShowUploadModal(false);
    setShowSavePathModal(false);
    setPathToSave('');
    setPathNameToSave('');
  }, [selectedOrg]);

  const listFiles = async (overridePath?: string) => {
    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const path = overridePath || pathInput || '/';
      const response = await api.post('auth/files/list', {
        organization_id: selectedOrg,
        service_type: serviceType.toUpperCase(),
        path: path,
      });

      if (response.status === 200 && response.data) {
        const backendFiles = response.data.files || [];

        // Map backend file structure to frontend format
        const mappedFiles: FileItem[] = backendFiles.map((file: any) => ({
          name: file.name,
          type: file.type === 'directory' ? 'folder' : 'file',
          size: file.size || '-',
          modified: formatUtcToIst(file.modified || '-', file.modified_timestamp),
          owner: '-', // Backend doesn't provide owner
          permissions: '-', // Backend doesn't provide permissions
          size_bytes: file.size_bytes || 0,
          modified_timestamp: file.modified_timestamp || 0,
        }));

        setFiles(mappedFiles);
        setCurrentPath(response.data.current_path || path);
        setPathInput(response.data.current_path || path);
      }
    } catch (error: any) {
      console.error('Error listing files:', error);
      setError(error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };
  const navigateToFolder = (folderName: string) => {
    let newPath: string;

    if (!currentPath || currentPath === '') {
      // If no current path, start from root
      newPath = `/${folderName}`;
    } else {
      // Add current path to history before navigating
      setPathHistory([...pathHistory, currentPath]);
      newPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`;
    }

    // Update state
    setPathInput(newPath);
    setCurrentPath(newPath);

    listFiles(newPath);
  };

  const goBack = () => {
    if (pathHistory.length > 0) {
      const previousPath = pathHistory[pathHistory.length - 1];
      setPathHistory(pathHistory.slice(0, -1));
      setPathInput(previousPath);
      setCurrentPath(previousPath);

      listFiles(previousPath);
    }
  };

  const downloadFile = async (fileName: string) => {
    if (!selectedOrg || !currentPath) {
      setError('Please select an organization and navigate to a path first');
      return;
    }

    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_ENDPOINT;
      const accessToken = getUserToken()?.access || '';

      const response = await axios.post(
        `${apiUrl}/auth/files/download`,
        {
          organization_id: selectedOrg,
          service_type: serviceType.toUpperCase(),
          path: currentPath,
          file_name: fileName,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          responseType: 'blob',
        },
      );

      // Create a blob from the response
      const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });

      // Extract filename from Content-Disposition header if available, otherwise use provided fileName
      const contentDisposition = response.headers['content-disposition'];
      let downloadFileName = fileName;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          downloadFileName = filenameMatch[1];
        }
      }

      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setError('Access denied: You do not have permission to download this file');
      } else {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to download file';
        setError(errorMessage);
      }
    }
  };

  const downloadSelected = async () => {
    if (selectedFiles.size === 0) {
      setError('No files selected');
      return;
    }

    if (!selectedOrg || !currentPath) {
      setError('Please select an organization and navigate to a path first');
      return;
    }

    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_ENDPOINT;
      const accessToken = getUserToken()?.access || '';

      const response = await axios.post(
        `${apiUrl}/auth/files/download-multiple`,
        {
          organization_id: selectedOrg,
          service_type: serviceType.toUpperCase(),
          path: currentPath,
          file_names: Array.from(selectedFiles),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          responseType: 'blob',
        },
      );

      // Create a blob from the ZIP response
      const blob = new Blob([response.data], { type: 'application/zip' });

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let zipFilename = `files_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          zipFilename = filenameMatch[1];
        }
      }

      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setError('Access denied: You do not have permission to download these files');
      } else {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to download files';
        setError(errorMessage);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPendingUploads(files);
    setShowUploadModal(true);
  };

  const uploadFiles = async () => {
    if (!selectedOrg || !currentPath) {
      setError('Please select an organization and navigate to a path first');
      return;
    }

    setError(null);
    setShowUploadModal(false);

    const uploadPromises = pendingUploads.map((file) => {
      return new Promise<void>((resolve, reject) => {
        const fileId = `${file.name}-${Date.now()}`;
        setUploadProgress((prev: Record<string, UploadProgressItem>) => ({
          ...prev,
          [fileId]: { name: file.name, progress: 0, status: 'uploading' },
        }));

        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('organization_id', selectedOrg);
          formData.append('service_type', serviceType.toUpperCase());
          formData.append('path', currentPath);
          formData.append('file_name', file.name);

          const apiUrl = process.env.REACT_APP_API_ENDPOINT;
          const accessToken = getUserToken()?.access || '';

          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 100);
              setUploadProgress((prev) => ({
                ...prev,
                [fileId]: { ...prev[fileId], progress },
              }));
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status === 200) {
              try {
                const response = JSON.parse(xhr.responseText);
                if (response.status === 200) {
                  setUploadProgress((prev) => ({
                    ...prev,
                    [fileId]: { ...prev[fileId], progress: 100, status: 'complete' },
                  }));
                  // Clean up after 3 seconds
                  setTimeout(() => {
                    setUploadProgress((prev) => {
                      const newProgress = { ...prev };
                      delete newProgress[fileId];
                      return newProgress;
                    });
                  }, 3000);
                  resolve();
                } else {
                  const errorMsg = response.message || 'Upload failed';
                  setUploadProgress((prev) => ({
                    ...prev,
                    [fileId]: { ...prev[fileId], status: 'error', error: errorMsg },
                  }));
                  reject(new Error(errorMsg));
                }
              } catch (parseError) {
                setUploadProgress((prev) => ({
                  ...prev,
                  [fileId]: { ...prev[fileId], status: 'error', error: 'Failed to parse response' },
                }));
                reject(new Error('Failed to parse response'));
              }
            } else if (xhr.status === 403) {
              const errorMsg = 'Access denied: You do not have permission to upload files to this location';
              setUploadProgress((prev) => ({
                ...prev,
                [fileId]: { ...prev[fileId], status: 'error', error: errorMsg },
              }));
              setError(errorMsg);
              reject(new Error(errorMsg));
            } else {
              try {
                const errorResponse = JSON.parse(xhr.responseText);
                const errorMsg = errorResponse.message || `Upload failed with status ${xhr.status}`;
                setUploadProgress((prev) => ({
                  ...prev,
                  [fileId]: { ...prev[fileId], status: 'error', error: errorMsg },
                }));
                reject(new Error(errorMsg));
              } catch {
                const errorMsg = `Upload failed with status ${xhr.status}`;
                setUploadProgress((prev) => ({
                  ...prev,
                  [fileId]: { ...prev[fileId], status: 'error', error: errorMsg },
                }));
                reject(new Error(errorMsg));
              }
            }
          });

          xhr.addEventListener('error', () => {
            setUploadProgress((prev) => ({
              ...prev,
              [fileId]: { ...prev[fileId], status: 'error', error: 'Network error occurred' },
            }));
            reject(new Error('Network error occurred'));
          });

          xhr.addEventListener('abort', () => {
            setUploadProgress((prev) => ({
              ...prev,
              [fileId]: { ...prev[fileId], status: 'error', error: 'Upload cancelled' },
            }));
            reject(new Error('Upload cancelled'));
          });

          xhr.open('POST', `${apiUrl}/auth/files/upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
          xhr.send(formData);
        } catch (error: any) {
          const errorMsg = error?.message || 'Upload failed';
          setUploadProgress((prev) => ({
            ...prev,
            [fileId]: { ...prev[fileId], status: 'error', error: errorMsg },
          }));
          reject(error);
        }
      });
    });

    try {
      await Promise.all(uploadPromises);
      // Refresh file list after all uploads complete
      listFiles();
    } catch (error: any) {
      const errorMessage = error?.message || 'Some uploads failed';
      if (!errorMessage.includes('Network error') && !errorMessage.includes('cancelled')) {
        setError(errorMessage);
      }
    }

    setPendingUploads([]);
  };

  const toggleFileSelection = (fileName: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileName)) {
      newSelection.delete(fileName);
    } else {
      newSelection.add(fileName);
    }
    setSelectedFiles(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedFiles.size === files.filter((f: FileItem) => f.type === 'file').length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(files.filter((f: FileItem) => f.type === 'file').map((f: FileItem) => f.name)));
    }
  };

  const openSavePathModal = () => {
    setPathToSave(currentPath);
    // Check if path already exists and pre-fill name
    const existingPath = savedPaths[selectedOrg]?.find(
      (sp) => sp.path === currentPath && sp.service_type === serviceType.toUpperCase(),
    );
    setPathNameToSave(existingPath?.name || '');
    setShowSavePathModal(true);
  };

  const savePath = async () => {
    if (!selectedOrg || !pathToSave) {
      setError('Please select an organization and enter a path');
      return;
    }

    setError(null);

    try {
      const response = await api.post('auth/paths/save', {
        organization_id: selectedOrg,
        service_type: serviceType.toUpperCase(),
        path: pathToSave,
        name: pathNameToSave || '',
      });

      if (response.status === 200 && response.saved_paths) {
        setSavedPaths((prev) => ({
          ...prev,
          [selectedOrg]: response.saved_paths || [],
        }));
        setShowSavePathModal(false);
        setPathNameToSave('');
      }
    } catch (error: any) {
      console.error('Failed to save path:', error);
      setError(typeof error === 'string' ? error : 'Failed to save path');
    }
  };

  const loadSavedPath = (path: string) => {
    setPathInput(path);
    setCurrentPath('');
    // Automatically browse to the saved path
    listFiles(path);
  };

  const deleteSavedPath = async (path: string) => {
    if (!isAdmin) {
      setError('Only administrators can remove saved paths');
      return;
    }
    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }

    setError(null);

    try {
      const response = await api.post('auth/paths/delete', {
        organization_id: selectedOrg,
        service_type: serviceType.toUpperCase(),
        path: path,
      });

      if (response.status === 200 && response.saved_paths) {
        setSavedPaths((prev) => ({
          ...prev,
          [selectedOrg]: response.saved_paths || [],
        }));
      }
    } catch (error: any) {
      console.error('Failed to delete path:', error);
      setError(typeof error === 'string' ? error : 'Failed to delete path');
    }
  };

  const currentOrgSavedPaths =
    selectedOrg && savedPaths[selectedOrg]
      ? savedPaths[selectedOrg].filter((sp) => sp.service_type === serviceType.toUpperCase())
      : [];

  return (
    <Flex vertical gap={24} style={{ marginTop: 16 }}>
      <Flex vertical gap={8}>
        <Title level={2}>File Browser</Title>
        <Text>Access and manage your SFTP and S3 files</Text>
      </Flex>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-red-900 font-semibold mb-1">Error</h4>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-700">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-gray-900 font-semibold mb-3">Upload Progress</h4>
          <div className="space-y-2">
            {Object.entries(uploadProgress).map(([id, upload]) => {
              const uploadItem = upload as UploadProgressItem;
              return (
                <div key={id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-900 text-sm">{uploadItem.name}</span>
                    <span
                      className={`text-sm ${
                        uploadItem.status === 'complete'
                          ? 'text-green-600'
                          : uploadItem.status === 'error'
                            ? 'text-red-600'
                            : 'text-blue-600'
                      }`}
                    >
                      {uploadItem.status === 'complete'
                        ? '✓ Complete'
                        : uploadItem.status === 'error'
                          ? `✗ ${uploadItem.error}`
                          : `${uploadItem.progress}%`}
                    </span>
                  </div>
                  {uploadItem.status === 'uploading' && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadItem.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Connection Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4 mb-4">
          <Select
            value={selectedOrg}
            onChange={setSelectedOrg}
            placeholder="Select Organization"
            style={{ width: '200px', height: '40px' }}
            showSearch
            optionFilterProp="label"
            options={organizations.map((org) => ({
              label: org.name,
              value: org.account_id,
            }))}
          />

          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 h-[40px]">
              <input
                type="radio"
                value="sftp"
                checked={serviceType === 'sftp'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceType(e.target.value as 'sftp' | 's3')}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 font-medium">SFTP</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 h-[40px]">
              <input
                type="radio"
                value="s3"
                checked={serviceType === 's3'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceType(e.target.value as 'sftp' | 's3')}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 font-medium">S3</span>
            </label>
          </div>
          <input
            type="text"
            placeholder="/path/to/directory"
            value={pathInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPathInput(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && listFiles()}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-[40px]"
            style={{ height: '40px' }}
          />
          <button
            onClick={() => listFiles()}
            disabled={loading || !selectedOrg}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
          >
            {loading ? 'Loading...' : 'Browse'}
          </button>
          {currentPath && (
            <button
              onClick={openSavePathModal}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
              title="Save current path"
            >
              Save Path
            </button>
          )}
        </div>

        {/* Saved Paths */}
        {selectedOrg && currentOrgSavedPaths.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Bookmark className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-semibold text-gray-700">Saved Paths ({serviceType.toUpperCase()})</span>
            </div>
            <div
              className="flex gap-2 overflow-x-auto"
              style={{ flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' }}
            >
              {currentOrgSavedPaths.map((savedPath: SavedPath, index: number) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg group"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'fit-content' }}
                >
                  <button
                    onClick={() => loadSavedPath(savedPath.path)}
                    className="text-sm text-blue-700 hover:text-blue-800 font-medium whitespace-nowrap"
                    title={savedPath.path}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {savedPath.name || savedPath.path}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => deleteSavedPath(savedPath.path)}
                      className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove saved path"
                      style={{ flexShrink: 0 }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Path Navigation */}
      {currentPath && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <FolderOpen className="w-5 h-5 text-gray-600" />
            <span className="text-gray-900 font-medium">Current Path:</span>
            <span className="text-blue-600 font-mono">{currentPath}</span>
          </div>
          <div className="flex items-center gap-2">
            {pathHistory.length > 0 && (
              <button
                onClick={goBack}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                ← Back
              </button>
            )}
          </div>
        </div>
      )}

      {/* Actions Bar */}
      {currentPath && (
        <div className="flex items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {selectedFiles.size > 0
                ? `${selectedFiles.size} selected`
                : `${files.filter((f: FileItem) => f.type === 'file').length} files`}
            </span>
            <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!currentPath}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors font-medium"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <button
              onClick={downloadSelected}
              disabled={selectedFiles.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              <Download className="w-4 h-4" />
              Download Selected
            </button>
          </div>
        </div>
      )}

      {/* File Table */}
      {files.length > 0 && (
        <Table
          dataSource={filteredFiles}
          columns={[
            // {
            //   title: (
            //     <input
            //       type="checkbox"
            //       checked={
            //         selectedFiles.size === files.filter((f: FileItem) => f.type === 'file').length &&
            //         files.filter((f: FileItem) => f.type === 'file').length > 0
            //       }
            //       onChange={toggleSelectAll}
            //       className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
            //     />
            //   ),
            //   key: 'select',
            //   width: 50,
            //   render: (_: any, record: FileItem) => {
            //     if (record.type === 'file') {
            //       return (
            //         <input
            //           type="checkbox"
            //           checked={selectedFiles.has(record.name)}
            //           onChange={() => toggleFileSelection(record.name)}
            //           className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
            //         />
            //       );
            //     }
            //     return null;
            //   },
            // },
            {
              title: 'Name',
              key: 'name',
              render: (_: any, record: FileItem) => (
                <div className="flex items-center gap-3">
                  {record.type === 'folder' ? (
                    <FolderOpen className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <File className="w-5 h-5 text-blue-500" />
                  )}
                  {record.type === 'folder' ? (
                    <button
                      onClick={() => navigateToFolder(record.name)}
                      className="text-gray-900 hover:text-blue-600 transition-colors font-medium flex items-center gap-1"
                    >
                      {record.name}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <span className="text-gray-900">{record.name}</span>
                  )}
                </div>
              ),
            },
            {
              title: 'Size',
              dataIndex: 'size',
              key: 'size',
              render: (size: string) => <span className="text-sm text-gray-600">{size}</span>,
            },
            {
              title: 'Modified',
              dataIndex: 'modified',
              key: 'modified',
              render: (modified: string) => <span className="text-sm text-gray-600">{modified}</span>,
            },
            {
              title: 'Actions',
              key: 'actions',
              align: 'center',
              render: (_: any, record: FileItem) => {
                const maxDownloadSizeBytes = 50 * 1024 * 1024; // 50 MB
                const canDownload = record.type === 'file' && (record.size_bytes ?? 0) < maxDownloadSizeBytes;

                return (
                  <div className="flex justify-center gap-2">
                    {canDownload && (
                      <button
                        onClick={() => downloadFile(record.name)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                        title="Download file"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              },
            },
          ]}
          rowKey={(record, index) => `${record.name}-${index}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
        />
      )}

      {files.length === 0 && !loading && currentPath && !error && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No files found in this directory</p>
        </div>
      )}

      {!currentPath && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Select an organization and enter a path to browse files</p>
          <p className="text-gray-500 text-sm">Choose between SFTP or S3 and click Browse to get started</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Confirm Upload</h3>
            <p className="text-gray-700 mb-4">Upload {pendingUploads.length} file(s) to:</p>
            <div className="bg-gray-50 rounded p-3 mb-4 border border-gray-200">
              <p className="text-blue-600 font-mono text-sm">{currentPath}</p>
            </div>
            <div className="mb-4 max-h-48 overflow-y-auto">
              {pendingUploads.map((file: File, index: number) => (
                <div key={index} className="text-gray-700 text-sm py-1">
                  • {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={uploadFiles}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
              >
                Upload
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setPendingUploads([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Path Modal */}
      {showSavePathModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Save Path</h3>
            <p className="text-gray-700 mb-4">Save this path for quick access in the future:</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization:{' '}
                <span className="text-gray-900 font-semibold">
                  {organizations.find((org) => org.account_id === selectedOrg)?.name || selectedOrg}
                </span>
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Type: <span className="text-gray-900 font-semibold">{serviceType.toUpperCase()}</span>
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">Path</label>
              <input
                type="text"
                value={pathToSave}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPathToSave(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono mb-3"
                placeholder="/path/to/directory"
              />
              <label className="block text-sm font-medium text-gray-700 mb-2">Name (optional)</label>
              <input
                type="text"
                value={pathNameToSave}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPathNameToSave(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter a name for this path"
              />
              <p className="text-xs text-gray-500 mt-1">If left empty, the path will be used as the name</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={savePath}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setShowSavePathModal(false);
                  setPathToSave('');
                  setPathNameToSave('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Flex>
  );
};

export default FileAccessDashboard;
